import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { useAuth } from '../context/CompleteAuthContext';
import { toast } from 'sonner';
import { CheckCircle, AlertCircle, RefreshCw, Users } from 'lucide-react';

import { API_URL as BACKEND_URL } from '../config/api';

const InviteAccept = ({ onGroupJoined }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState('loading'); // loading, success, error, auth_required, expired
  const [errorMessage, setErrorMessage] = useState('');
  const handledRef = useRef(false);

  useEffect(() => {
    // Wait for auth state to resolve before deciding
    if (isLoading) return;
    if (handledRef.current) return;

    const params = new URLSearchParams(location.search);
    const token = params.get('token');

    if (!token) {
      handledRef.current = true;
      setStatus('error');
      setErrorMessage('Invalid invitation link — no token provided.');
      return;
    }

    if (!isAuthenticated) {
      handledRef.current = true;
      // Persist invite URL so login/signup/OAuth flows can return here.
      const currentUrl = window.location.pathname + window.location.search;
      sessionStorage.setItem('redirect_after_login', currentUrl);
      setStatus('auth_required');
      return;
    }

    handledRef.current = true;
    (async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/groups/join/${token}`, {
          method: 'POST',
          credentials: 'include',
        });

        if (response.ok) {
          const data = await response.json();
          toast.success('You joined the group', {
            description: 'Opening the group now…',
          });
          setStatus('success');
          // Auto-route directly into the joined group.
          // AppContent uses internal state for routing, so stash the group id
          // and bounce home — AppContent will read it on mount and open the group.
          setTimeout(() => {
            if (data.group_id) {
              try { sessionStorage.setItem('open_group_id', data.group_id); } catch { /* ignore */ }
            }
            if (onGroupJoined && data.group_id) {
              onGroupJoined(data.group_id);
            } else {
              navigate('/');
            }
          }, 800);
          return;
        }

        // Parse the error to show a helpful message
        let detail = '';
        try {
          const err = await response.json();
          detail = typeof err.detail === 'string' ? err.detail : '';
        } catch { /* ignore */ }

        if (response.status === 400 && detail.toLowerCase().includes('expired')) {
          setStatus('expired');
          return;
        }

        // Auto-attach may already have activated the user. Send them to the dashboard with a friendly toast.
        toast.info('Invitation could not be activated', {
          description: detail || 'You may already be a member. Check your groups.',
        });
        setTimeout(() => navigate('/'), 1200);
      } catch (e) {
        console.error('Invite activation failed:', e);
        toast.error('Could not process invitation', {
          description: 'Please try again or contact the group owner.',
        });
        setTimeout(() => navigate('/'), 1200);
      }
    })();
  }, [isLoading, isAuthenticated, location, navigate, onGroupJoined]);

  const handleSignIn = () => {
    // redirect_after_login already set above; just go to landing/auth
    navigate('/');
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Card className="p-8 text-center max-w-md w-full" data-testid="invite-loading">
          <RefreshCw className="w-12 h-12 mx-auto mb-4 animate-spin text-blue-600" />
          <h2 className="text-xl font-semibold mb-2">Processing invitation…</h2>
          <p className="text-gray-600">Hold tight — adding you to the group.</p>
        </Card>
      </div>
    );
  }

  if (status === 'auth_required') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Card className="p-8 text-center max-w-md w-full" data-testid="invite-auth-required">
          <Users className="w-12 h-12 mx-auto mb-4 text-blue-600" />
          <h2 className="text-xl font-semibold mb-2">Sign in to join the group</h2>
          <p className="text-gray-600 mb-6">
            Create an account or sign in to accept this invitation. We'll bring you straight back to the group.
          </p>
          <Button
            onClick={handleSignIn}
            className="w-full bg-blue-600 hover:bg-blue-700"
            data-testid="invite-signin-btn"
          >
            Sign in / Sign up
          </Button>
        </Card>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Card className="p-8 text-center max-w-md w-full" data-testid="invite-success">
          <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-600" />
          <h2 className="text-2xl font-bold mb-2">You're in!</h2>
          <p className="text-gray-600">Opening your group…</p>
        </Card>
      </div>
    );
  }

  if (status === 'expired') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Card className="p-8 text-center max-w-md w-full" data-testid="invite-expired">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-amber-500" />
          <h2 className="text-2xl font-bold mb-2">This invitation has expired</h2>
          <p className="text-gray-600 mb-6">
            Ask the group owner to send you a fresh invite link.
          </p>
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="w-full"
            data-testid="invite-expired-back"
          >
            Go to dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <Card className="p-8 text-center max-w-md w-full" data-testid="invite-error">
        <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-600" />
        <h2 className="text-2xl font-bold mb-2">Unable to join group</h2>
        <p className="text-gray-600 mb-6">
          {errorMessage || 'The invitation link may be invalid or expired.'}
        </p>
        <Button
          onClick={() => navigate('/')}
          variant="outline"
          className="w-full"
          data-testid="invite-error-back"
        >
          Go to dashboard
        </Button>
      </Card>
    </div>
  );
};

export default InviteAccept;
